import React, { useEffect, useState, useMemo } from "react";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import {
  Row,
  Col,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
} from "reactstrap";
import { useParams } from "react-router-dom";
import { useGetTenantUserDetail } from "../../api/users/service";
import GeneralInfo from '../../components/UserDetail/GeneralInfo';
import SecurityPermissions from '../../components/UserDetail/SecurityPermissions';
import VendorUserSettings from '../../components/UserDetail/VendorUserSettings';
import UserInfoCards from "views/components/Users/UserInfoCards";
import { getActiveColorStyle } from "../../utils/colorUtils";
import { useTheme2FormPage, Theme2FormFieldsStyle, THEME2_ADMIN_INSETS } from "hooks/useTheme2FormPage";
import { THEME2_SIDEBAR_NAV_FONT_SIZE_VAR } from "utils/sidebarShellTheme";
import { Button } from "reactstrap";
import { useNavigate } from "react-router-dom";
import SubHeader from "components/Navbars/SubHeader";
import { useClearListStateOnLeave, LIST_STATE_FLOWS } from "utils/listStateSessionStorage";

const USERS_LIST_STATE_KEY = "dealertiq-users-list-state";

const initialForm = {
  email: "",
  alt_email: "",
  password: "",
  full_name: "",
  tenant_name: "",
  tenant_status: true,
  salutation: "",
  suffix: "",
  title: "",
  work_phone: "",
  home_phone: "",
  cell_phone: "",
  fax: "",
};

const UserDetail = ({ activeColor = "secondary", shellThemeVariant = "1", isLightTheme = false }) => {
  const { id } = useParams();
  const { t } = useTranslation();
  useClearListStateOnLeave(USERS_LIST_STATE_KEY, LIST_STATE_FLOWS.users);
  const activeColorStyle = useMemo(() => getActiveColorStyle(activeColor), [activeColor]);
  const {
    isTheme2,
    inputStyle,
    inputClassName,
    selectStyles,
    theme2FieldsCss: baseTheme2FieldsCss,
    formFieldsClassName,
    contentClassName,
  } = useTheme2FormPage({
    shellThemeVariant,
    activeColorStyle,
    pageKey: "user-detail",
    insetClass: THEME2_ADMIN_INSETS.users,
    withSelectStyles: true,
  });

  const theme2FieldsCss = useMemo(
    () =>
      isTheme2
        ? `${baseTheme2FieldsCss}
          .user-detail-page.user-detail-page--theme2 .user-detail-tabs .nav-link {
            font-size: ${THEME2_SIDEBAR_NAV_FONT_SIZE_VAR} !important;
          }
          .user-detail-page.user-detail-page--theme2 .user-detail-form-fields table th,
          .user-detail-page.user-detail-page--theme2 .user-detail-form-fields table td {
            font-size: ${THEME2_SIDEBAR_NAV_FONT_SIZE_VAR} !important;
          }
          .user-detail-page.user-detail-page--theme2 .user-detail-form-fields .card-header b,
          .user-detail-page.user-detail-page--theme2 .user-detail-form-fields h5,
          .user-detail-page.user-detail-page--theme2 .user-detail-form-fields h6 {
            font-size: calc(${THEME2_SIDEBAR_NAV_FONT_SIZE_VAR} + 2px) !important;
          }
          @media (max-width: 991.98px) {
            .sidebar.sidebar-shell-theme-2 ~ .main-panel > .content.user-detail-page.user-detail-page--theme2.theme2-admin-content-compact.theme2-users-inset {
              padding-top: calc(74px + 45px) !important;
            }
          }
          @media (max-width: 575.98px) {
            .sidebar.sidebar-shell-theme-2 ~ .main-panel > .content.user-detail-page.user-detail-page--theme2.theme2-admin-content-compact.theme2-users-inset {
              padding-top: calc(64px + 45px) !important;
            }
          }`
        : "",
    [isTheme2, baseTheme2FieldsCss]
  );

  const { data: userDetail } = useGetTenantUserDetail(id || "");
  const [formData, setFormData] = useState(initialForm);
  const [activeTab, setActiveTab] = useState("1");
  const [isSaving, setIsSaving] = useState(false);
  const navigate = useNavigate();

  const generalInfoRef = React.useRef();
  const securityPermissionsRef = React.useRef();

  const handleSave = async () => {
    setIsSaving(true);
    try {
      if (activeTab === "1" && generalInfoRef.current) {
        await generalInfoRef.current.handleSave();
      } else if (activeTab === "2" && securityPermissionsRef.current) {
        await securityPermissionsRef.current.handleSave();
      }
    } catch (error) {
      console.error("Error saving user detail:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    navigate(-1);
  };

  // No currency formatting needed for user info cards

  useEffect(() => {
    if (userDetail) {
      setFormData({
        ...formData,
        email: userDetail.email || "",
        full_name: userDetail.full_name || "",
        work_phone: userDetail.work_phone || "",
        home_phone: userDetail.home_phone || "",
        cell_phone: userDetail.cell_phone || "",
        confirmed: userDetail.confirmed,
        title: userDetail.title || "",
        salutation: userDetail.salutation || "",
        suffix: userDetail.suffix || "",
        alt_email: userDetail.alt_email || "",
        fax: userDetail.fax || "",
      });
    }
  }, [userDetail]);

  return (
    <div className={contentClassName}>
      <Theme2FormFieldsStyle css={theme2FieldsCss} />
      <SubHeader
        activeColor={activeColor}
        back={"/users"}
        title={(userDetail?.full_name || id)}
        status={userDetail?.status}
        theme2ScrollGapFill={isTheme2}
        isLightTheme={isLightTheme}
      />
      <Row className="justify-content-center">
        <Col md="12">
          <div
            className={classNames("user-detail-info-cards", {
              "users-info-cards-theme2": isTheme2,
              "users-info-cards-theme2--light": isTheme2 && isLightTheme,
              "users-info-cards-theme2--dark": isTheme2 && !isLightTheme,
            })}
          >
            <UserInfoCards
              data={userDetail}
              getActiveColorStyle={() => activeColorStyle}
              isTheme2={isTheme2}
              isLightTheme={isLightTheme}
              alwaysExpanded={isTheme2}
            />
          </div>
          <div className="w-100 user-detail-tabs">
            <div className="d-flex flex-column flex-lg-row align-items-lg-center justify-content-between">
              <Nav tabs className="border-0" style={{ borderBottom: "none" }}>
                <NavItem>
                  <NavLink
                    href="#"
                    className={activeTab === "1" ? "active font-weight-bold" : "text-muted"}
                    style={{
                      background: "none",
                      border: "none",
                      position: "relative",
                      cursor: 'pointer',
                      color: activeTab === "1" ? activeColorStyle.borderColor : undefined,
                      fontWeight: activeTab === "1" ? 700 : 500

                    }}
                    onClick={(e) => {
                      e.preventDefault();
                      setActiveTab("1");
                    }}
                  >
                    {t('userDetail.tabs.generalInfo')}
                    {activeTab === "1" && (
                      <div style={{ height: 3, background: activeColorStyle.borderColor, position: "absolute", left: 0, right: 0, bottom: -8 }} />
                    )}
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink
                    href="#"
                    className={activeTab === "2" ? "active font-weight-bold" : "text-muted"}
                    style={{
                      background: "none",
                      border: "none",
                      position: "relative",
                      cursor: 'pointer',
                      color: activeTab === "2" ? activeColorStyle.borderColor : undefined,
                      fontWeight: activeTab === "2" ? 700 : 500
                    }}
                    onClick={(e) => {
                      e.preventDefault();
                      setActiveTab("2");
                    }}
                  >
                    {t('userDetail.tabs.securityPermissions')}
                    {activeTab === "2" && (
                      <div style={{ height: 3, background: activeColorStyle.borderColor, position: "absolute", left: 0, right: 0, bottom: -8 }} />
                    )}
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink
                    disabled={true}
                    href="#"
                    className={activeTab === "3" ? "active font-weight-bold" : "text-muted"}
                    style={{
                      background: "none",
                      border: "none",
                      position: "relative",
                      cursor: 'pointer',
                      color: activeTab === "3" ? activeColorStyle.borderColor : undefined,
                      fontWeight: activeTab === "3" ? 700 : 500
                    }}
                    onClick={(e) => {
                      e.preventDefault();
                      setActiveTab("3");
                    }}
                  >
                    {t('userDetail.tabs.vendorUserSettings')}
                    {activeTab === "3" && (
                      <div style={{ height: 3, background: activeColorStyle.borderColor, position: "absolute", left: 0, right: 0, bottom: -8 }} />
                    )}
                  </NavLink>
                </NavItem>
              </Nav>

              <div className="d-flex align-items-center ml-auto">
                <Button onClick={handleCancel} color="secondary" outline size="sm" className="mr-2">
                  {t('userDetail.generalInfo.buttons.cancel')}
                </Button>
                <Button
                  style={{ ...activeColorStyle, color: 'white' }}
                  size="sm"
                  onClick={handleSave}
                  disabled={isSaving}
                >
                  {isSaving ? t('userDetail.generalInfo.buttons.saving') : t('userDetail.generalInfo.buttons.save')}
                </Button>
              </div>
            </div>
          </div>
          <TabContent activeTab={activeTab} className={formFieldsClassName}>
            <TabPane tabId="1">
              <GeneralInfo
                ref={generalInfoRef}
                userDetail={userDetail}
                activeColor={activeColor}
                isTheme2={isTheme2}
                inputStyle={inputStyle}
                inputClassName={inputClassName}
                selectStyles={selectStyles}
              />
            </TabPane>
            <TabPane tabId="2">
              <SecurityPermissions
                ref={securityPermissionsRef}
                userDetail={userDetail}
                activeColor={activeColor}
                isTheme2={isTheme2}
                inputStyle={inputStyle}
                inputClassName={inputClassName}
                selectStyles={selectStyles}
              />
            </TabPane>
            <TabPane tabId="3">
              <VendorUserSettings userDetail={userDetail} />
            </TabPane>
          </TabContent>
        </Col>
      </Row>
    </div>
  );
};

export default UserDetail; 